import { Autocomplete, Button, DialogActions, DialogContent, DialogTitle, Modal, ModalDialog, Textarea, Typography } from "@mui/joy";
import React from "react";
import WrappedButton from "../WrappedButton";
import { Mechanic } from "../Interfaces/Mechanic";

interface ReassignPackingListDialogProps {
	open: boolean;
	packingListId: string;
	currentMechanic: string;
	onClose: () => void;
	onSaved: () => void;
}

/**
 * Dialog component for reassigning a packing list to a different mechanic
 * Includes mechanic selection and optional notes field
 */
export default function ReassignPackingListDialog(props: ReassignPackingListDialogProps) {
	const [mechanics, setMechanics] = React.useState<Array<Mechanic>>([]);
	const [selectedMechanic, setSelectedMechanic] = React.useState<Mechanic | null>(null);
	const [notes, setNotes] = React.useState("");
	const [isSaving, setIsSaving] = React.useState(false);

	// Fetch mechanics list when dialog opens
	React.useEffect(() => {
		if (props.open) {
			getMechanics();
			setSelectedMechanic(null);
			setNotes("");
		}
	}, [props.open]);

	/**
	 * Fetches the list of available mechanics from the API
	 */
	const getMechanics = () => {
		fetch("User/GetPackingListAssignees", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({}),
		})
			.then((x) => x.json())
			.then((x) => setMechanics(x));
	};

	/**
	 * Saves the reassignment via API call
	 */
	const saveReassignment = () => {
		if (!selectedMechanic) {
			alert("Please select a mechanic.");
			return;
		}

		setIsSaving(true);
		fetch("PackingList/ReassignPackingList", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				packingListId: props.packingListId,
				newMechanicId: selectedMechanic.users_id,
				notes: notes || null,
			}),
		}).then(() => {
			setIsSaving(false);
			props.onSaved();
		});
	};

	return (
		<Modal open={props.open}>
			<ModalDialog sx={{ width: 500 }}>
				<DialogTitle>Reassign Work Order</DialogTitle>
				<DialogContent>
					<Typography level="body-sm" sx={{ mb: 2 }}>
						Current Mechanic: <strong>{props.currentMechanic || "Unassigned"}</strong>
					</Typography>
					<Typography level="body-sm" sx={{ mb: 1 }}>
						Select New Mechanic:
					</Typography>
					<Autocomplete
						options={mechanics}
						getOptionLabel={(opt) => opt.full_name}
						openOnFocus
						placeholder="Select Mechanic"
						value={selectedMechanic}
						onChange={(event, value) => setSelectedMechanic(value)}
						sx={{ mb: 2 }}
					/>
					<Typography level="body-sm" sx={{ mb: 1 }}>
						Reassignment Notes (optional):
					</Typography>
					<Textarea
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
						minRows={3}
						placeholder="Enter reason for reassignment..."
						sx={{ width: "100%" }}
					/>
				</DialogContent>
				<DialogActions>
					<WrappedButton onClick={saveReassignment} busy={isSaving}>
						Reassign
					</WrappedButton>
					<Button onClick={props.onClose} color="primary">
						Cancel
					</Button>
				</DialogActions>
			</ModalDialog>
		</Modal>
	);
}
